/**
 * File:        resources/js/components/ui/badge.tsx
 * Module:      Design System · Primitives
 * Purpose:     shadcn Badge primitive with semantic brand variants — success / warning /
 *              info / danger / brand, each available in subtle (default) and solid modes.
 *
 * Exports:
 *   - Badge(spanProps)               — the Badge component
 *   - badgeVariants                  — cva variant factory
 *
 * Variant matrix (semantic × tone):
 *   - default   → primary tokens
 *   - secondary → neutral tokens
 *   - destructive → destructive tokens
 *   - outline   → bordered, transparent
 *   - brand     → brand-50 bg / brand-700 text (subtle); solid → brand-600 bg / white text
 *   - success   → positive-soft (subtle) / positive (solid)
 *   - warning   → warning-soft (subtle) / warning (solid)
 *   - info      → brand-50 (subtle) / brand-500 (solid)
 *
 * Side-effects:
 *   - none
 *
 * Key invariants:
 *   - Each variant supports both the "subtle" tone (used as default) and the "solid" tone
 *     via the `tone` cva dimension.
 *   - When rendered as an <a> (via Slot/asChild), hover states are also retuned.
 *
 * Author:      Aman Vats Sharma
 * Last-updated: 2026-06-17
 */
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        brand:
          "border-transparent bg-brand-50 text-brand-700 [a&]:hover:bg-brand-100 dark:bg-brand-950 dark:text-brand-300 dark:[a&]:hover:bg-brand-900",
        success:
          "border-transparent bg-positive/10 text-positive [a&]:hover:bg-positive/20 dark:bg-positive/20 dark:text-positive-foreground dark:[a&]:hover:bg-positive/30",
        warning:
          "border-transparent bg-warning/10 text-warning-foreground [a&]:hover:bg-warning/20 dark:bg-warning/20 dark:text-warning-foreground dark:[a&]:hover:bg-warning/30",
        info:
          "border-transparent bg-brand-50 text-brand-700 [a&]:hover:bg-brand-100 dark:bg-brand-950 dark:text-brand-300 dark:[a&]:hover:bg-brand-900",
        danger:
          "border-transparent bg-destructive/10 text-destructive [a&]:hover:bg-destructive/20 dark:bg-destructive/20 dark:text-destructive-foreground dark:[a&]:hover:bg-destructive/30",
      },
      tone: {
        subtle: "",
        solid: "",
      },
    },
    compoundVariants: [
      {
        variant: "brand",
        tone: "solid",
        class: "bg-brand-600 text-white [a&]:hover:bg-brand-700",
      },
      {
        variant: "success",
        tone: "solid",
        class: "bg-positive text-white [a&]:hover:bg-positive/90",
      },
      {
        variant: "warning",
        tone: "solid",
        class: "bg-warning text-white [a&]:hover:bg-warning/90",
      },
      {
        variant: "info",
        tone: "solid",
        class: "bg-brand-500 text-white [a&]:hover:bg-brand-600",
      },
      {
        variant: "danger",
        tone: "solid",
        class: "bg-destructive text-white [a&]:hover:bg-destructive/90",
      },
    ],
    defaultVariants: {
      variant: "default",
      tone: "subtle",
    },
  }
)

export interface BadgeProps
  extends React.ComponentProps<"span">,
    VariantProps<typeof badgeVariants> {
  asChild?: boolean
}

function Badge({
  className,
  variant,
  tone,
  asChild = false,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      data-tone={tone}
      className={cn(badgeVariants({ variant, tone }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }